// CTV-682: "What you'll need" — the walkthrough handed to an advertiser who
// has decided to start. Revised against station feedback (KITV, Aug 2026),
// which called this one "good communication" and asked for small edits; the
// version it replaced is kept in ./originals.
//
// What the revision changed:
//   • Step 1's website bullet no longer explains what we do with the address.
//     It raised a question the reader can't act on yet, and step 3 answers it.
//   • "Dates" became "Flight dates", the station's own term.
//   • Step 5 is plainer: add your card, nothing is charged until approval.
//   • Reporting says a full performance report, updated daily.
//
// The five steps still mirror the wizard exactly (business → campaign →
// commercial → verify → review & pay). The wizard SKIPS step 4 for an
// already-signed-in buyer and renumbers Review from 5 to 4 (see
// wizardSteps.ts); paper can't be conditional, so step 4 is worded as
// something that happens rather than a screen they will definitely see.

import React from 'react';
import { QRCodeSVG } from 'qrcode.react';
import type { PublisherConfig } from '../../../../../types/selfService';
import MaterialSheet from '../../MaterialSheet';
import { storefrontHref, storefrontUrl } from '../../storefrontUrl';
import { displayedName } from '../../../../../data/publisherConfigs';

const GuideOnePagerV2: React.FC<{ config: PublisherConfig; refCode?: string | null }> = ({
  config,
  refCode,
}) => {
  const name = displayedName(config);

  return (
    <MaterialSheet
      config={config}
      variant="brochure"
      kicker={<>For our advertisers<br />Before you start</>}
      footNote={`© ${name} · Streaming TV placements in your local market`}
    >
      <div className="msheet-eyebrow">How it works</div>
      <h1>
        <span>What you&rsquo;ll need.</span>
        <em>And what to expect.</em>
      </h1>
      <p className="msheet-sub">
        Have your website and a credit card handy. <b>That&rsquo;s all you need.</b>
      </p>

      <div className="msheet-mid">
        <div className="msheet-steps msheet-steps--rows msheet-steps--dense">
          <div className="msheet-step">
            <div className="msheet-num">1</div>
            <h3>Tell us about your business</h3>
            <ul>
              <li>Your business name and contact details</li>
              <li>Your website address</li>
            </ul>
          </div>
          <div className="msheet-step">
            <div className="msheet-num">2</div>
            <h3>Set your campaign</h3>
            <ul>
              <li>
                <b>Budget:</b> Enter the budget you want to spend
              </li>
              <li>
                <b>Flight dates:</b> Run your ad for as long as you like
              </li>
              <li>
                <b>Coverage:</b> The whole {name} market, or narrowed to the specific areas you
                care about
              </li>
            </ul>
          </div>
          <div className="msheet-step">
            <div className="msheet-num">3</div>
            <h3>Add your commercial</h3>
            <ul>
              <li>Upload a 15 or 30 second commercial if you already have one</li>
              <li>
                Or let us <b>build one free</b> from your website, and customize it until you are
                happy with it
              </li>
            </ul>
          </div>
          <div className="msheet-step">
            <div className="msheet-num">4</div>
            <h3>Confirm your email</h3>
            <ul>
              <li>We send a code to your inbox</li>
              <li>Your receipt, approval, and campaign updates all go there</li>
            </ul>
          </div>
          <div className="msheet-step">
            <div className="msheet-num">5</div>
            <h3>Review and pay</h3>
            <ul>
              <li>Just add your credit card</li>
              <li>No charge occurs until your ad is approved</li>
            </ul>
          </div>
        </div>

        <div className="msheet-why msheet-why--compact">
          <div>
            <div className="msheet-why-label">What happens next</div>
            <h4>We review it, then you&rsquo;re live</h4>
            <p>{name} checks your commercial, usually in a day or two</p>
          </div>
          <div>
            <div className="msheet-why-label">Once it&rsquo;s running</div>
            <h4>Track every play</h4>
            {/* "Updated daily" rather than "provided daily": the report lives in
                the dashboard and refreshes on a daily cron. Nothing is emailed,
                so "provided" would imply a delivery we don't make. */}
            <p>A full report on ad performance, updated daily</p>
          </div>
        </div>

        <div className="msheet-cta msheet-cta--tight">
          <div className="msheet-cta-text">
            <div className="msheet-cta-label">Start here</div>
            <div className="msheet-cta-main">
              <strong>Ready when you are.</strong>
              <a href={storefrontHref(config.slug, refCode)} target="_blank" rel="noreferrer">
                {storefrontUrl(config.slug, refCode)}
              </a>
            </div>
          </div>
          <div className="msheet-cta-qr">
            <QRCodeSVG value={storefrontHref(config.slug, refCode)} size={62} level="M" />
          </div>
        </div>
      </div>
    </MaterialSheet>
  );
};

export default GuideOnePagerV2;
